import React, { useState } from 'react';
import { supabase } from '../lib/supabaseClient';

const DynamicForm = ({ data, onChange }) => {
  const [uploadingState, setUploadingState] = useState({});

  if (data === null || data === undefined) return null;

  const handleChange = (key, value) => {
    onChange({ ...data, [key]: value });
  };

  const handleImageUpload = async (file, keyPath, onChangeCallback) => {
    if (!file) return;

    // Use uploadingState with full path to prevent overlapping states
    const pathStr = keyPath.join('.');
    setUploadingState(prev => ({ ...prev, [pathStr]: true }));

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2, 15)}_${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, file);

      if (uploadError) {
        throw uploadError;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filePath);

      onChangeCallback(publicUrl);
    } catch (error) {
      console.error('Error uploading image:', error.message);
      alert('Error uploading image: ' + error.message);
    } finally {
      setUploadingState(prev => ({ ...prev, [pathStr]: false }));
    }
  };

  const renderField = (key, value, path = []) => {
    const fieldPath = [...path, key].join('.');

    // Handle Arrays (like lists of objects or strings)
    if (Array.isArray(value)) {
      return (
        <div key={fieldPath} className="mb-6 p-4 border border-sand-200 rounded-xl bg-white dark:bg-brand-slate shadow-sm">
          <h3 className="font-bold text-lg mb-4 text-[#333] dark:text-[#fff] capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</h3>
          {value.map((item, index) => (
            <div key={`${fieldPath}-${index}`} className="mb-4 p-4 border-l-4 border-brand-rose bg-sand-50 dark:bg-brand-slate/50 rounded-r-lg">
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium text-sm text-brand-clay uppercase tracking-wider">Item {index + 1}</span>
                <button
                  onClick={() => {
                    const newArray = [...value];
                    newArray.splice(index, 1);
                    handleChange(key, newArray);
                  }}
                  className="text-red-500 text-xs hover:underline"
                >
                  Remove
                </button>
              </div>

              {/* Recursive call for objects inside arrays, or simple input for strings */}
              {typeof item === 'object' && item !== null ? (
                <DynamicForm
                  data={item}
                  onChange={(updatedItem) => {
                    const newArray = [...value];
                    newArray[index] = updatedItem;
                    handleChange(key, newArray);
                  }}
                />
              ) : (
                <input
                  type="text"
                  value={item}
                  onChange={(e) => {
                    const newArray = [...value];
                    newArray[index] = e.target.value;
                    handleChange(key, newArray);
                  }}
                  className="w-full p-2 border border-sand-200 rounded-lg bg-white dark:bg-brand-slate/80 text-[#333] dark:text-[#fff] focus:ring-2 focus:ring-brand-rose outline-none"
                />
              )}
            </div>
          ))}
          <button
            onClick={() => {
              const emptyItem = typeof value[0] === 'object'
                ? Object.keys(value[0] || {}).reduce((acc, k) => ({...acc, [k]: ''}), {})
                : '';
              handleChange(key, [...value, emptyItem]);
            }}
            className="text-sm text-brand-rose font-medium hover:underline flex items-center gap-1"
          >
            + Add New Item
          </button>
        </div>
      );
    }

    // Handle nested Objects
    if (typeof value === 'object' && value !== null) {
      return (
        <div key={fieldPath} className="mb-6 p-4 border border-sand-200 rounded-xl bg-sand-50 dark:bg-brand-slate/50">
          <h3 className="font-bold text-lg mb-4 text-[#333] dark:text-[#fff] capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</h3>
          <DynamicForm
            data={value}
            onChange={(updatedObj) => handleChange(key, updatedObj)}
          />
        </div>
      );
    }

    // Handle simple primitives (strings, numbers, booleans)
    // Use textarea for long strings (like descriptions or paragraphs)
    const isLongText = typeof value === 'string' && value.length > 50;

    // Detect if this key is likely an image URL
    const isImageField = typeof value === 'string' && (key.toLowerCase().includes('image') || key.toLowerCase().includes('photo') || key.toLowerCase().includes('img'));

    // Don't show numeric IDs as editable fields to avoid breaking React keys
    if (key === 'id') return null;

    return (
      <div key={fieldPath} className="mb-4">
        <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300 capitalize">
          {key.replace(/([A-Z])/g, ' $1').trim()}
        </label>
        {isImageField ? (
          <div className="space-y-2">
            <input
              type="text"
              value={value}
              onChange={(e) => handleChange(key, e.target.value)}
              placeholder="Or enter image URL here"
              className="w-full p-2 border border-sand-200 rounded-lg bg-white dark:bg-brand-slate/80 text-[#333] dark:text-[#fff] focus:ring-2 focus:ring-brand-rose outline-none text-sm"
            />
            <div className="flex items-center gap-3">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    handleImageUpload(e.target.files[0], [...path, key], (newUrl) => handleChange(key, newUrl));
                  }
                }}
                className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-medium file:bg-brand-rose file:text-white hover:file:bg-brand-rose/90 transition-all cursor-pointer"
              />
              {uploadingState[fieldPath] && <span className="text-sm text-brand-rose font-medium animate-pulse">Uploading...</span>}
            </div>
            {value && value.startsWith('http') && (
              <img src={value} alt="Preview" className="mt-2 h-20 w-auto rounded border border-sand-200 object-cover" />
            )}
          </div>
        ) : isLongText ? (
          <textarea
            value={value}
            onChange={(e) => handleChange(key, e.target.value)}
            className="w-full p-3 border border-sand-200 rounded-lg bg-white dark:bg-brand-slate/80 text-[#333] dark:text-[#fff] focus:ring-2 focus:ring-brand-rose outline-none min-h-[100px]"
          />
        ) : (
          <input
            type={typeof value === 'number' ? 'number' : typeof value === 'boolean' ? 'checkbox' : 'text'}
            checked={typeof value === 'boolean' ? value : undefined}
            value={typeof value === 'boolean' ? undefined : value}
            onChange={(e) => {
              const val = typeof value === 'boolean' ? e.target.checked :
                         typeof value === 'number' ? Number(e.target.value) :
                         e.target.value;
              handleChange(key, val);
            }}
            className={`w-full p-2 border border-sand-200 rounded-lg bg-white dark:bg-brand-slate/80 text-[#333] dark:text-[#fff] focus:ring-2 focus:ring-brand-rose outline-none ${typeof value === 'boolean' ? 'w-auto' : ''}`}
          />
        )}
      </div>
    );
  };

  return (
    <div className="space-y-2">
      {Object.entries(data).map(([key, value]) => renderField(key, value))}
    </div>
  );
};

export default DynamicForm;